<template>
  <fts-popper 
    :append-to-body="appendToBody"
    :placement="placement"
    strategy-fixed
    :offset="offset"
    content-class="emoji-popper-dropdown-menu"
    :stop-propagation="stopPropagation"
    :disabled="disabled"
    @select="selectEmoji"
    @created="created"
    @hide="hide"
    class="fts-emoji">
    <div 
      class="emoji-dropdown-content" 
      slot-scope="{ select }"
      :style="{ width: `${width}px`, height: `${height}px` }">
      <!-- Header -->
      <div class="emoji-category-menu">
        <div class="category-title" :class="{ selected: selectedEmojiCategory === 'All' }">
          <span class="all-selection" @click="selectEmojiCategory('All')">All</span>
        </div>
        <div
          class="category-title"
          v-for="emojiGroup in emojiList"
          :key="emojiGroup.id"
          :class="{ selected: selectedEmojiCategory === emojiGroup.name }"
          @click="selectEmojiCategory(emojiGroup.name)">
          <fts-moji :size="23" :emoji="getCategoryIcon(emojiGroup.name)" />
        </div>
      </div>
      <!-- Search -->
      <div class="emoji-search" v-if="searchInput || randomBtn">
        <input
          type="text"
          placeholder="🔎 Search Emoji"
          v-model="searchText"
          v-if="searchInput"
          ref="searchInput" />

        <fts-btn color="primary" boxShadowNone @click.native.prevent="select('random')">
          {{getEmojiBtnText}}
        </fts-btn>

        <fts-btn color="danger" boxShadowNone @click.native.prevent="select" v-if="emoji">
          Clear
        </fts-btn>
      </div>
      <!-- Content -->
      <div class="emoji-content fts-scrollbar">
        <div
          class="emoji-wrapper"
          v-if="searchText && selectedEmojiCategory === 'All'">
          <div class="emoji-title-wrapper">
            <div class="emoji-title">
              <span>Search Result</span>
            </div>
          </div>
          <div
            class="emoji-item"
            v-for="emoji in getAllEmojiList"
            :key="emoji.id"
            @mouseenter="showEmojiDetail(emoji)"
            @mouseleave="showEmojiDetail('default')"
            @click="select(emoji)">
            <fts-moji :emoji="emoji.char" />
          </div>
          <div
            class="emoji-not-found"
            v-if="getAllEmojiList.length === 0"
          ></div>
        </div>

        <div
          class="emoji-wrapper"
          v-for="emojiGroup in getEmojiGroup"
          :key="emojiGroup.id"
          v-else>
          <div class="emoji-title-wrapper">
            <div class="emoji-title">
              {{ searchText ? "Search Result" : emojiGroup.name }}
            </div>
            <div>
              <span class="emoji-count">{{
                getEmojiList(emojiGroup.group).length
              }}</span>
            </div>
          </div>
          <div
            class="emoji-item"
            v-for="emoji in getEmojiList(emojiGroup.group)"
            :key="emoji.id"
            @mouseenter="showEmojiDetail(emoji)"
            @mouseleave="showEmojiDetail('default')"
            @click="select(emoji)">
            <span>
              <fts-moji :emoji="emoji.char"></fts-moji>
            </span>
          </div>
          <div
            class="emoji-not-found"
            v-if="getEmojiList(emojiGroup.group).length === 0"
          ></div>
        </div>
      </div>
      <!-- Footer -->
      <div class="emoji-footer" v-if="emojiFooter">
        <div class="emoji-icon">
          <fts-moji :size="30" :emoji="emojiHover.char" />
        </div>
        <div>
          <div class="emoji-name">{{ emojiHover.name }}</div>
          <div class="emoji-keywords">{{ emojiHover.keywords }}</div>
        </div>
      </div>   
    </div>
    <!-- Trigger -->
    <div
      slot="reference"
      class="emoji-toggle"
      :class="[
        { 'emoji-toggle--default': !emoji },
        { 'emoji-toggle--disabled': disabled }
      ]"
    >
      <fts-moji :size="toggleSize" :emoji="emojiDisplay" />
    </div>
  </fts-popper>
</template>

<script>
import FTSPopper from "./FTSPopperNew";
import EmojiList from "../../json/emoji-group.json";
import FTSMoji from "./FTSMoji";
import FTSBtn from "./FTSBtn";

export default {
  components: {
    "fts-popper": FTSPopper,
    "fts-moji": FTSMoji,
    "fts-btn": FTSBtn
  },
  props: {
    appendToBody: Boolean,
    width: {
      type: Number,
      default: 600,
    },
    height: {
      type: Number,
      default: 450,
    },
    // Emoji
    emoji: String,
    emojiFooter: {
      type: Boolean,
      default: true,
    },
    defaultEmoji: {
      type: String,
      default: "😀",
    },
    emojiList: {
      type: Array,
      default: () => EmojiList,
    },
    toggleSize: Number,
    // Element
    searchInput: {
      type: Boolean,
      default: true,
    },
    randomBtn: {
      type: Boolean,
      default: true,
    },
    btnTextList: {
      type: Array,
      default: () => [
        "Stunning Emoji",
        "Surprise Me",
        "Fabulous Emoji",
        "Try Me",
      ],
    },
    stopPropagation: Boolean,
    placement: {
      type: String,
      default: 'bottom-start'
    },
    offset: {
      type: Array,
      default: () => [0, 6]
    },
    disabled: Boolean,
  },
  model: {
    prop: "emoji",
    event: "updateEmoji",
  },
  data: () => ({
    selectedEmojiCategory: "All",
    searchText: "",
    emojiHover: {
      char: "☝",
      name: "Select your emoji",
    },
  }),
  methods: {
    created() {
      if (!this.disabled) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus();
        });
      }
    },
    hide() {
      this.selectedEmojiCategory = "All"
      this.searchText = ""
    },
    getCategoryIcon(item) {
      var category = item.toLowerCase();
      switch (category) {
        case "smileys & people":
          return "😃";
        case "animals & nature":
          return "🦁";
        case "food & drink":
          return "🎂";
        case "travel & places":
          return "✈";
        case "activity":
          return "🏀";
        case "objects":
          return "💡";
        case "symbols":
          return "❤";
        case "flags":
          return "🚩";
      }
      return "❗️";
    },
    getEmojiList(elements) {
      return elements.filter((element) => {
        return element.keywords
          .toLowerCase()
          .includes(this.searchText.toLowerCase()) || 
          element.name.toLowerCase()
          .includes(this.searchText.toLowerCase());
      });
    },
    selectEmojiCategory(item) {
      if (this.searchText) {
        this.searchText = "";
      }
      this.selectedEmojiCategory = item;
    },
    selectEmoji(item) {
      if (item === "random") item = _.sample(this.getAllEmojiList);

      this.$emit("updateEmoji", item.char);
    },
    showEmojiDetail(item) {
      if (item != "default") {
        this.emojiHover = item;
        if (this.emojiHover.keywords)
          this.emojiHover.keywords = this.emojiHover.keywords
            .split("|")
            .join(" , ");
      } else {
        this.emojiHover = { char: "☝", name: "Select your emoji" };
      }
    }
  },
  computed: {
    emojiDisplay() {
      return this.emoji ? this.emoji : this.defaultEmoji;
    },
    getEmojiGroup() {
      if (this.selectedEmojiCategory != "All") {
        return this.emojiList.filter((element) => {
          return element.name === this.selectedEmojiCategory;
        });
      } else {
        return this.emojiList;
      }
    },
    getAllEmojiList() {
      var allEmojiList = [];
      this.emojiList.forEach((element) => {
        allEmojiList = allEmojiList.concat(element.group);
      });
      return this.getEmojiList(allEmojiList);
    },
    getEmojiBtnText() {
      return _.sample(this.btnTextList);
    }
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$emojiTheme: var(--fts-gray-2);

.fts-emoji {
  display: flex;
  .emoji-toggle {
    @include user-select-none;
    &:not(.emoji-toggle--disabled) {
      transition: all 0.5s ease-out;
      &:hover {
        cursor: pointer;
        filter: grayscale(0);
        opacity: 1;
      }
    }
    &.emoji-toggle--default {
      filter: grayscale(1);
      opacity: .5;
    }
  }
}

.emoji-popper-dropdown-menu.popper-content {
  padding: 0;
  .emoji-dropdown-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    .emoji-category-menu {
      padding: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: space-around;
      background: $emojiTheme;
      .category-title {
        @include user-select-none;
        -webkit-filter: grayscale(1);
        filter: grayscale(1);
        font-size: 1.2rem;
        transition: filter 0.5s ease-in-out;
        &:hover {
          cursor: pointer;
        }
        &.selected {
          -webkit-filter: grayscale(0);
          filter: grayscale(0);
        }
        .all-selection {
          color: $primaryColor;
          font-weight: bold;
        }
      }
    }
    .emoji-search {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 0.375rem;
      padding: 0.375rem;
      input {
        flex: 2;
        display: block;
        margin: calc(0.375rem / 2);
        padding: 0.375rem 0.75rem;
        height: $height;
        line-height: 1.5;
        font-size: 0.75rem;
        border: 1px solid var(--fts-form-control-border);
        border-radius: .25rem;
        background-clip: padding-box;
        background-color: var(--fts-form-control-background);
        color: var(--fts-form-control);
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        &:focus {
          border-color: $primaryColor;
          outline: 0;
          -webkit-box-shadow: $inputBoxShadow;
          box-shadow: $inputBoxShadow;
        }
      }
      button {
        height: $height;
        margin: calc(0.375rem / 2);
        font-size: 0.75rem;
      }
    }
    .emoji-content {
      flex: 1 1 auto;
      padding: 0 0.475rem;
      overflow-y: auto;
      overflow-x: hidden;
      .emoji-wrapper {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 16px;
        .emoji-title-wrapper {
          position: -webkit-sticky;
          position: sticky;
          top: 0;
          display: flex;
          padding-bottom: 0.175rem;
          background: var(--fts-popover-background);
          width: 100%;
          .emoji-title {
            font-size: 1.2rem;
            font-weight: bold;
          }
          .emoji-count {
            margin: 0 0.375rem;
            padding: 0.175rem 0.375rem;
            border-radius: 10em;
            background: $primaryColor;
            color: #fff;
            font-size: 0.675rem;
          }
        }
        .emoji-item {
          padding: 5px;
          width: 44px;
          font-size: 24px;
          cursor: pointer;
          text-align: center;
        }
        .emoji-not-found {
          &::before {
            content: "😅 We couldn't found that emoji";
            font-size: 25px;
          }
        }
      }
    }
    .emoji-footer {
      @media (max-width: 767.98px) {
        display: none;
      }
      min-height: 70px;
      padding: 12px 24px;
      display: flex;
      align-items: center;
      background: $emojiTheme;
      .emoji-icon {
        margin-right: 24px;
        font-size: 1.5rem;
      }
      .emoji-name {
        font-size: 1.2rem;
        font-weight: bold;
      }
      .emoji-keywords {
        font-size: 0.75rem;
      }
    }
  }
}
</style>